import React, { useState } from "react";
import Button from "react-bootstrap/Button";
import "bootstrap/dist/css/bootstrap.min.css";
import App from "./App";

function Project3() {
  const [main, setMain] = useState(false);
  const gotoMainPage = () => {
    setMain(true);
  };

  return (
    <>
      {!main ? (
        <div className="body">
          <div className="headerP1">
            <h2 id="Title">Image Filtering And Edge Detection</h2>
            <div id="P1Github">
              <a
                href="https://github.com/AkhilTheBoss/Image-Filtering-And-Edge-Detection"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="secondary">Github</Button>
              </a>
            </div>
            <div id="mainPage">
              <Button variant="primary" onClick={gotoMainPage}>
                Main Page
              </Button>
            </div>
          </div>
          <br />
          <h4 className="Heading">Goal</h4>
          <p className="Content">
            The purpose of this project is to explore different image processing
            techniques.
          </p>
          <h4 className="Heading">Important Terminologies</h4>
          <p className="Content">
            <ul>
              <li>
                Gaussian Filter: The primary purpose is to reduce noise and
                smoothen the image.
              </li>
              <li>
                Noise: unwanted or random variations in the pixel values of an
                image.
              </li>
              <li>
                Sigma: defines the amount of blurring. Large sigma value will
                cause large blurring and small sigma will cause small blurring.
              </li>
              <li>
                Alpha: Alpha can make the sharpening effect more or less
                pronounced. A higher alpha value will result in a stronger
                sharpening effect, while a lower alpha value will produce a
                milder effect.
              </li>
              <li>
                Sobel Filter: It's used to highlight the edges or boundaries
                within an image.
              </li>
              <li>
                Magnitude: The magnitude helps identify the edges in an image by
                highlighting areas with significant changes in brightness or
                color, which are often indicative of object boundaries or
                features. A high magnitude value indicates a strong edge, where
                there's a rapid transition from one pixel intensity to another.
                Conversely, a low magnitude value suggests a weak or subtle
                edge, where the transition between pixel intensities is gradual.
              </li>
              <li>
                Orientation: It refers to the direction or angle of an edge
                within an image.
              </li>
              <li>
                Resampled image: Resizing an image by reducing or increasing its
                number of pixels
              </li>
              <li>
                Interpolation: These algorithms calculate the values of pixels
                based on the surrounding pixels. Interpolation fills in the gaps
                between data points by estimating values based on the
                surrounding known data.
              </li>
            </ul>
          </p>
          <h4 className="Heading">Explanation</h4>
          <p className="Content">
            <ul>
              <li>
                Implemented a gaussian blur algorithm to smooth images and
                reduce noise. This involves image convolution using a 2D
                Gaussian Kernel to create a blurring effect.
                <div>Steps involved</div>
                <ul>
                  <li>
                    Creating a Gaussian Filter (Gaussian Kernel): The size of
                    the gaussian filter is found by the formula, filter size = 2
                    * int(4 * &sigma; + 0.5) + 1 and the value of each pixel in
                    the gaussian filter is found by the formula, G(x, y) = (
                    {`1 / (2πσ²)`}) e<sup>{`-((x² + y²) / (2σ²))`}</sup>
                  </li>
                  <li>
                    Convolution Operation: A convolution filter can be thought
                    of as a sliding window that moves across the image, applying
                    the kernel to small regions of the image. The convolution
                    operation is performed by element-wise multiplying the
                    kernel with the region of the image that is currently under
                    the window, and then summing the results.
                  </li>
                </ul>
              </li>
              <li>
                Developed a more efficient approach to gaussian blur using
                separable filters. This involved breaking 2D gaussian filters
                into two 1D filters (horizontal filter and vertical filter). The
                two gaussian filters were separately applied to the image and
                the combined result was a gaussian blur. This technique is much
                faster as it reduced computational cost (less number of
                computations).
                <div>
                  g<sub>&sigma;</sub> = g<sub>&sigma;</sub> <sup>x</sup> x g
                  <sub>&sigma;</sub> <sup>y</sup>
                </div>
                <div>
                  g<sub>&sigma;</sub> <sup>x</sup> (x, y)= ({`1 / (2πσ²)`}) e
                  <sup>{`-(x² / (2σ²))`}</sup>
                </div>
                <div>
                  g<sub>&sigma;</sub> <sup>y</sup> (x, y)= ({`1 / (2πσ²)`}) e
                  <sup>{`-(y² / (2σ²))`}</sup>
                </div>
              </li>
              <li>
                Create a sharpening image function that enhances image details
                using the formula img_sharpen = (image - (alpha * (smoothed -
                image))). Multiplying with alpha in the sharpening formula
                allows you to control the strength of the sharpening effect. By
                adjusting the value of alpha, you can make the sharpening effect
                more or less pronounced. A higher alpha value will result in a
                stronger sharpening effect, while a lower alpha value will
                produce a milder effect. This parameter gives you fine-grained
                control over how much you want to enhance the image details.
              </li>
              <li>
                I employed Sobel filters for image edge detection. The process
                involved calculating and presenting both the magnitude and
                orientation of the resulting image. Specifically, I utilized a
                horizontal Sobel filter represented by the matrix
                [[-1,0,1],[-2,0,2],[-1,0,1]] and a vertical Sobel filter
                represented by [[-1,-2,-1],[0,0,0],[1,2,1]]. These filters were
                independently applied to two identical images, resulting in two
                separate images, namely imagefilteredX and imagefilteredY. To
                determine the magnitude, I utilized the formula: magnitude =
                &radic;( imagefilteredX<sup>2</sup>+ imagefilteredY<sup>2</sup>)
                and to determine the direction, I utilized the formula:
                orientation = tan<sup>-1</sup>(imagefilteredY/imagefilteredX)
              </li>
              <li>
                Used NN interpolation and Bilateral interpolation techniques for
                upsampling:
                <ul>
                  <li>
                    NN interpolation: When you need to rescale an image, NN
                    interpolation selects the pixel value from the nearest
                    neighboring pixel in the original image. It assigns the
                    value of the nearest pixel to the corresponding pixel in the
                    new image. The resulting image may appear blocky or
                    pixelated, especially when scaling up, because it directly
                    copies pixel values without considering the surrounding
                    pixels.
                  </li>
                  <li>
                    Bilateral interpolation: It takes into account not only the
                    nearest pixel but also the surrounding pixels in both the
                    original and the resampled image. It uses a weighted average
                    of neighboring pixel values based on their spatial distance
                    and intensity similarity. Bilateral interpolation produces
                    smoother and more visually pleasing results, especially when
                    scaling up images. It helps reduce artifacts and jagged
                    edges that can be seen in NN interpolation.
                  </li>
                </ul>
              </li>
              <li>
                Implemented Bilateral filtering to preseve edges. It reduces
                noice and smoothens the image and alos retains the sharp
                transitions or edges in an image (for example: borders). The
                filter accounts two factors when filtering a pixel: spatial
                proximity and pixel intensity similarity.
                <ul>
                  <li>
                    Spatial proximity: The distance between the center pixel and
                    the neighboring pixels. Pixels that are closer in space have
                    more influence on each other.
                  </li>
                  <li>
                    Pixel intensity similarity: The difference in pixel values
                    (intensities) between the center pixel and neighboring
                    pixels. Pixels with similar intensities have more influence
                    on each other.
                  </li>
                </ul>
              </li>
            </ul>
          </p>
        </div>
      ) : (
        <App />
      )}
    </>
  );
}

export default Project3;
